import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { bizWorkspaceAPI as bizAPI } from '../services/bizApi';
import { BizShell, useBizStyles } from './BizSetupPage';

const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function BizInvoicesPage() {
  const bs = useBizStyles();
  const [invoices, setInvoices] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ customer_email: '', customer_name: '', description: '', amount: '', days_until_due: 14 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setInvoices((await bizAPI.listInvoices()).data.invoices); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await bizAPI.createInvoice({ ...form, amount: parseFloat(form.amount), days_until_due: parseInt(form.days_until_due, 10) });
      toast.success('Invoice sent');
      if (data.hosted_invoice_url) window.open(data.hosted_invoice_url, '_blank');
      setForm({ customer_email: '', customer_name: '', description: '', amount: '', days_until_due: 14 });
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || 'Failed to send'); }
    finally { setBusy(false); }
  };

  const inputClass = 'w-full rounded-xl px-3 h-11 text-sm focus:outline-none transition-colors';

  return (
    <BizShell title="Invoices" subtitle="Professional Stripe invoices in under 60 seconds." testId="biz-invoices-page">
      <form onSubmit={submit} className="p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3" style={bs.cardStyle} data-testid="invoice-form">
        <input type="email" required placeholder="Client email" value={form.customer_email} onChange={(e) => setForm((f) => ({ ...f, customer_email: e.target.value }))} className={inputClass} style={bs.inputStyle} data-testid="inv-email" />
        <input placeholder="Client name (optional)" value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} className={inputClass} style={bs.inputStyle} data-testid="inv-name" />
        <input required placeholder="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={`${inputClass} sm:col-span-2`} style={bs.inputStyle} data-testid="inv-desc" />
        <input type="number" step="0.01" min="0.5" required placeholder="Amount (USD)" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className={inputClass} style={bs.inputStyle} data-testid="inv-amount" />
        <div className="flex gap-3">
          <input type="number" min="1" max="90" value={form.days_until_due} onChange={(e) => setForm((f) => ({ ...f, days_until_due: e.target.value }))} className={inputClass} style={bs.inputStyle} title="Days until due" data-testid="inv-due" />
          <button type="submit" disabled={busy} className="h-11 px-6 rounded-xl font-bold text-black text-sm whitespace-nowrap disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ background: bs.GREEN }} data-testid="inv-submit">
            {busy ? 'Sending…' : 'Send invoice'}
          </button>
        </div>
      </form>
      {err && <div className="p-4 text-sm text-[#F59E0B]" style={bs.cardStyle} data-testid="invoices-error">{err}</div>}
      {invoices && (
        <div style={bs.cardStyle} data-testid="invoices-list">
          <p className="text-[11px] tracking-[0.14em] uppercase font-semibold px-5 pt-5 pb-3" style={{ color: bs.labelColor }}>Sent invoices</p>
          {invoices.length === 0 ? (
            <p className="text-xs px-5 pb-5" style={{ color: bs.textFaint }}>None yet.</p>
          ) : invoices.map((i) => (
            <div key={i.invoice_id} className="px-5 py-3.5 flex items-center gap-3 text-sm transition-colors" style={{ borderTop: `1px solid ${bs.listItemBorder}` }}>
              <div className="flex-1 min-w-0">
                <p className="truncate" style={{ color: bs.textPrimary }}>{i.customer_email}</p>
                <p className="text-[10px] mt-0.5" style={{ color: bs.textMuted }}>{i.status} · due {i.due_date ? new Date(i.due_date * 1000).toLocaleDateString() : '—'}</p>
              </div>
              <p className="font-semibold tabular-nums" style={{ fontFamily: "'Bebas Neue', sans-serif", color: bs.textPrimary }}>{fmt(i.amount_due)}</p>
              {i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-xs font-semibold" style={{ color: bs.GREEN }}>View</a>}
            </div>
          ))}
        </div>
      )}
    </BizShell>
  );
}
